// HourlyVolumeChart — versão compacta do Heatmap (RF-01/03). Mesma entrada
// (HeatmapCellDto dia×hora), mas na linguagem do BarChart do Painel: colunas com
// altura % e cor var(--brand-green), agrupadas por dia da semana (Dom…Sáb) com
// separador visual entre dias. Ocupa bem menos altura que o heatmap. Sem libs
// externas (perf §13). A11y: cada barra é role="img" com aria-label descritivo
// (padrão do Heatmap) + tooltip hover.
import { useMemo, useState } from 'react';
import type { HeatmapCellDto } from '../api';
import styles from './HourlyVolumeChart.module.css';

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
// Janela 08h-20h (13 horas) — espelha o Heatmap.
const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i);
// Ticks esparsos pra não poluir: início/meio/fim da janela.
const HOUR_TICKS = new Set([8, 14, 20]);

export interface HourlyVolumeChartProps {
  cells: HeatmapCellDto[];
  /** Altura do plot interno em px. */
  height?: number;
  /** Texto pro aria-label/legenda do grupo. */
  caption?: string;
  /** Exibe a contagem acima da barra (só quando > 0). Default: false. */
  showValues?: boolean;
}

interface Tip {
  x: number;
  y: number;
  content: string;
}

export function HourlyVolumeChart({
  cells,
  height = 120,
  caption = 'Volume de conversas por dia da semana e hora',
  showValues = false,
}: HourlyVolumeChartProps) {
  const [tip, setTip] = useState<Tip | null>(null);

  const map = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells) m.set(`${c.dayOfWeek}:${c.hour}`, c.count);
    return m;
  }, [cells]);

  // Máximo GLOBAL entre todas as células — a altura das barras é comparável
  // entre dias diferentes (não normaliza por dia).
  const max = useMemo(() => Math.max(1, ...cells.map((c) => c.count)), [cells]);

  return (
    <figure className={styles.figure} role="group" aria-label={caption}>
      <div className={styles.plot} style={{ height }}>
        {DAYS.map((dayLabel, di) => (
          <div key={di} className={styles.dayGroup}>
            <div className={styles.bars}>
              {HOURS.map((h) => {
                const v = map.get(`${di}:${h}`) ?? 0;
                const pct = max > 0 ? (v / max) * 100 : 0;
                return (
                  <div key={h} className={styles.barSlot}>
                    {showValues && v > 0 && <span className={styles.barValue}>{v}</span>}
                    <div
                      role="img"
                      aria-label={`${dayLabel} ${h}h: ${v} conversas`}
                      title={`${dayLabel} ${h}h: ${v} conversas`}
                      className={styles.bar}
                      style={{ height: `${pct}%` }}
                      data-day={di}
                      data-hour={h}
                      data-count={v}
                      onMouseEnter={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setTip({
                          x: r.left + r.width / 2,
                          y: r.top,
                          content: `${dayLabel} ${h}h: ${v} conversas`,
                        });
                      }}
                      onMouseLeave={() => setTip(null)}
                    />
                  </div>
                );
              })}
            </div>
            <div className={styles.hourTicks} aria-hidden="true">
              {HOURS.map((h) => (
                <span key={h} className={styles.hourTick}>
                  {HOUR_TICKS.has(h) ? String(h).padStart(2, '0') : ''}
                </span>
              ))}
            </div>
            <span className={styles.dayLabel}>{dayLabel}</span>
          </div>
        ))}
      </div>
      {tip && (
        <div className={styles.tooltip} style={{ left: tip.x, top: tip.y }} role="tooltip">
          {tip.content}
        </div>
      )}
    </figure>
  );
}
